import React from "react";
import { Package, FileText, CheckCircle, ClipboardCheck } from "lucide-react";
import {
  AmendTransitPurchaseOrderFormAction,
  AmendTransitPurchaseOrderFormData,
  AmendTransitPurchaseOrderFormDataKey,
  TransitProductAmendmentInputField,
  TransitPurchaseOrder
} from "src/types";
import { ValidationErrors } from "src/errors";
import { useScrollOnMessage } from "src/hooks";
import { TransitProductAmendmentInputTable } from "src/components/tables/inputTables";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { TextInputBox } from "src/components/ui/inputs";
import { StatusPill } from "src/components/ui";
import { SubmissionTooltip } from "src/components/tooltips";
import { ControlButton } from "src/components/tables";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";

interface AmendTransitPurchaseOrderFormProps {
  formData: AmendTransitPurchaseOrderFormData;
  transitPurchaseOrderToAmend?: TransitPurchaseOrder;
  validationErrors?: ValidationErrors<AmendTransitPurchaseOrderFormDataKey>;
  formLvlErrorMessage: string;
  isFormComplete: boolean;
  handleInputChange: (
    field: AmendTransitPurchaseOrderFormDataKey,
    value: AmendTransitPurchaseOrderFormData[keyof AmendTransitPurchaseOrderFormData]
  ) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  setAction: (action: AmendTransitPurchaseOrderFormAction) => void;
}

const AmendTransitPurchaseOrderForm: React.FC<AmendTransitPurchaseOrderFormProps> = ({
  formData,
  transitPurchaseOrderToAmend,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  handleInputChange,
  handleSubmit,
  setAction
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSetProductAmendments: React.Dispatch<React.SetStateAction<TransitProductAmendmentInputField[]>> = (
    updatedProductAmendments
  ) => {
    handleInputChange("productAmendments", updatedProductAmendments as TransitProductAmendmentInputField[]);
  };

  const handleReceiveAll = () => {
    handleInputChange(
      "productAmendments",
      formData.productAmendments.map((productAmendment) => ({
        ...productAmendment,
        amendedReceivedQty: (productAmendment.confirmedQuantity ?? productAmendment.orderedQuantity ?? 0).toString()
      }))
    );
  };

  const handleReceiveLoggedQuantities = () => {
    handleInputChange(
      "productAmendments",
      formData.productAmendments.map((productAmendment) => ({
        ...productAmendment,
        amendedReceivedQty: (
          (productAmendment.warehouseLogQuantity ?? 0) + (productAmendment.childWarehouseLogQuantity ?? 0)
        ).toString()
      }))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Amend Transit Purchase Order</h1>
            <p className="text-gray-300 mt-2 text-lg">
              View product details and modify transit purchase order transit quantities
            </p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-6">
              {/* Search Section */}
              <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                  <Package className="w-4 h-4 mr-2" />
                  Purchase Order Search
                </h3>
                <TextInputBox
                  id="purchase-order-id"
                  placeholder="Purchase Order ID"
                  onChange={(value) => {
                    handleInputChange("purchaseOrderId", value.toUpperCase());
                  }}
                  value={formData.purchaseOrderId}
                />
                <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("purchaseOrderId")} />
              </div>

              {transitPurchaseOrderToAmend && (
                <>
                  {/* Status Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Purchase Order Status
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Confirmation Status */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Confirmation Status
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5">Vendor confirmation status</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {transitPurchaseOrderToAmend.confirmationStatus ? (
                            <StatusPill status={transitPurchaseOrderToAmend.confirmationStatus} />
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </div>

                      {/* Completion Status */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <ClipboardCheck className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Completion Status
                            </p>
                            <p className="text-sm text-gray-600 mt-0.5">Order completion status</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {transitPurchaseOrderToAmend.completionStatus ? (
                            <StatusPill status={transitPurchaseOrderToAmend.completionStatus} />
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Amendments Section */}
                  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
                    <h3 className="text-sm font-semibold text-black mb-4 tracking-wide uppercase flex items-center">
                      <FileText className="w-4 h-4 mr-2" />
                      Product Details & Received Quantity Amendments
                    </h3>
                    <TransitProductAmendmentInputTable
                      productAmendments={formData.productAmendments}
                      setProductAmendments={handleSetProductAmendments}
                    />
                    <ValidationErrorMessage
                      message={validationErrors?.getErrorMessageByFormDataKey("productAmendments")}
                    />
                    <TooltipProvider>
                      <div className="mt-4 flex gap-3">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <ControlButton onClick={handleReceiveAll} text="Received All" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs leading-relaxed">
                              Sets received quantities to match confirmed quantities (or ordered quantities if not
                              confirmed). This indicates all confirmed items have been received and the transit status
                              will be marked as closed.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <ControlButton onClick={handleReceiveLoggedQuantities} text="Receive Logged Quantities" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p className="text-xs leading-relaxed">
                              Sets received quantities to match the quantities logged by the warehouse during check-in.
                              This reflects the actual physical quantities received and recorded in the system.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </div>
                </>
              )}

              {/* Form Level Error */}
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />

              {/* Submit Button */}
              <div className="flex items-center gap-4">
                {!transitPurchaseOrderToAmend && (
                  <button
                    className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                    disabled={!isFormComplete}
                    onClick={() => setAction("FETCH")}
                    type="submit"
                  >
                    Search
                  </button>
                )}

                {transitPurchaseOrderToAmend && (
                  <>
                    <button
                      className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                      disabled={!isFormComplete}
                      onClick={() => setAction("AMEND")}
                      type="submit"
                    >
                      Save Amendments
                    </button>

                    {!isFormComplete && <SubmissionTooltip formState="NO_CHANGE" />}
                  </>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export { AmendTransitPurchaseOrderForm };
